/* ────────────────────────────────────────────────────────────────────────────
   Interview Report — ViewModel + Adapter layer
   ----------------------------------------------------------------------------
   Decouples the redesigned report UI from the raw `/get-interview/` response
   shape (InterviewSerializer). Components bind to `ReportViewModel` ONLY; when
   the backend contract changes, or richer evaluation data is added (confidence,
   coverage, objective health, evidence quality…), we update this adapter alone.

   HARD RULE: this adapter NEVER fabricates metrics. Every field is either taken
   verbatim from the API or derived by an explicit, honest computation over real
   values (e.g. a score band from a real score, an elapsed-seconds offset from
   two real timestamps). Absent data becomes `null`/`[]` so the UI can hide the
   section gracefully — it must never render placeholder analytics as if real.
   ──────────────────────────────────────────────────────────────────────────── */
export function scoreBand(pct) {
    if (pct == null || Number.isNaN(pct))
        return { tone: 'neutral', label: 'Not scored' };
    if (pct >= 80)
        return { tone: 'success', label: 'Strong' };
    if (pct >= 60)
        return { tone: 'warning', label: 'Moderate' };
    return { tone: 'danger', label: 'Needs work' };
}
// ── helpers ──────────────────────────────────────────────────────────────────
const asArray = (v) => (Array.isArray(v) ? v : []);
const asNum = (v) => v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v);
const pctOf = (score, denom) => {
    if (score == null || denom == null || denom === 0)
        return null;
    return Math.round((score / denom) * 100);
};
let _uid = 0;
const uid = (prefix) => `${prefix}-${_uid++}`;
const parseTime = (v) => {
    if (!v)
        return null;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? null : t;
};
// Map a Transcript.proctoring_insight dict → the per-answer VM (or null).
const mapInsight = (raw) => {
    if (!raw || typeof raw !== 'object' || !Object.keys(raw).length)
        return null;
    const integ = raw.integrity || {};
    const conf = raw.confidence || {};
    const attrs = conf.attributes || {};
    const win = raw.answer_window || {};
    return {
        brief: raw.brief || '',
        risk: integ.risk || null,
        flags: Array.isArray(integ.flags) ? integ.flags : [],
        confidenceBand: conf.band || null,
        confidenceScore: conf.score === null || conf.score === undefined ? null : Number(conf.score),
        attention: attrs.attentiveness === null || attrs.attentiveness === undefined
            ? null
            : Number(attrs.attentiveness),
        windowStart: win.start === undefined ? null : Number(win.start),
        windowEnd: win.end === undefined ? null : Number(win.end),
        durationSec: win.duration_sec === undefined ? null : Number(win.duration_sec),
        eventsCount: Array.isArray(integ.events) ? integ.events.length : 0,
    };
};
// Friendly names for the integrity flag types the backend emits.
const FLAG_LABELS = {
    looking_down: 'Looking down',
    looking_away: 'Looking away',
    eye_movement: 'Gaze off-screen',
    multiple_faces: 'Multiple faces',
    disappear_from_cam: 'Face not visible',
    hand_near_face: 'Hand near face',
    eyes_closed: 'Eyes closed',
    slouching: 'Slouching',
    talking: 'Mouth movement',
};
const attentionSignal = (attn) => {
    let value = 'Not measured';
    let tone = 'neutral';
    if (attn != null && !Number.isNaN(attn)) {
        if (attn >= 0.75) {
            value = 'Highly focused';
            tone = 'success';
        }
        else if (attn >= 0.55) {
            value = 'Attentive';
            tone = 'success';
        }
        else if (attn >= 0.35) {
            value = 'Wavering';
            tone = 'warning';
        }
        else {
            value = 'Distracted';
            tone = 'danger';
        }
    }
    return { key: 'attention', label: 'Attention', value, tone };
};
const confidenceSignal = (band) => {
    const b = (band || '').toLowerCase();
    let value = 'Not measured';
    let tone = 'neutral';
    if (b === 'high') {
        value = 'High';
        tone = 'success';
    }
    else if (b === 'moderate-high') {
        value = 'Moderate-high';
        tone = 'success';
    }
    else if (b === 'moderate') {
        value = 'Moderate';
        tone = 'warning';
    }
    else if (b === 'low') {
        value = 'Low';
        tone = 'danger';
    }
    else if (b) {
        value = b.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
        tone = 'neutral';
    }
    return { key: 'confidence', label: 'Confidence', value, tone };
};
const integritySignal = (risk, flags) => {
    const r = (risk || '').toLowerCase();
    const flagText = (flags || [])
        .map((f) => FLAG_LABELS[f] || f.replace(/_/g, ' '))
        .slice(0, 2)
        .join(', ');
    let value;
    let tone;
    if (r === 'high') {
        value = 'Review needed';
        tone = 'danger';
    }
    else if (r === 'medium') {
        value = 'Minor flags';
        tone = 'warning';
    }
    else {
        value = flagText ? 'Minor flags' : 'Clear';
        tone = flagText ? 'warning' : 'success';
    }
    if (flagText && value !== 'Clear')
        value = `${value} · ${flagText}`;
    return { key: 'integrity', label: 'Integrity', value, tone };
};
// Shared by the video overlay (time-synced) and the transcript (per-entry) —
// same three plain-English signals from the same raw insight either way.
export const signalsFromInsight = (ins) => [
    attentionSignal(ins.attention),
    confidenceSignal(ins.confidenceBand),
    integritySignal(ins.risk, ins.flags),
];
// Build per-answer signal windows from transcript insights that carry a time
// window. Used by the video overlay to show what the candidate was doing while
// answering the question playing at the current timestamp.
export const buildAnswerSignals = (transcript) => transcript
    .map((t) => t.proctoringInsight)
    .filter((ins) => !!ins && ins.windowStart != null && ins.windowEnd != null && ins.windowEnd > ins.windowStart)
    .map((ins) => ({
    start: ins.windowStart,
    end: ins.windowEnd,
    signals: signalsFromInsight(ins),
}))
    .sort((a, b) => a.start - b.start);
const fmtElapsed = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}m ${s.toString().padStart(2, '0')}s`;
};
const mmss = (sec) => {
    const s = Math.max(0, Math.floor(sec));
    return `${Math.floor(s / 60)
        .toString()
        .padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
};
const statusMeta = (status) => {
    switch ((status || '').toLowerCase()) {
        case 'pass':
            return { label: 'Passed', tone: 'success' };
        case 'fail':
            return { label: 'Not passed', tone: 'danger' };
        case 'completed':
            return { label: 'Completed', tone: 'neutral' };
        case 'in-progress':
            return { label: 'In progress', tone: 'warning' };
        case 'expired':
            return { label: 'Expired', tone: 'neutral' };
        case 'cancelled':
            return { label: 'Cancelled', tone: 'neutral' };
        case 'error':
            return { label: 'Processing error', tone: 'danger' };
        default:
            return { label: status || 'Scheduled', tone: 'neutral' };
    }
};
// Recommendation label derived HONESTLY from the backend Pass/Fail + score. We do
// not invent a hiring scale the backend doesn't have — this is a readable framing
// of the two real signals (status + final_score).
//
// Wording is deliberately screening-stage ("Shortlisted"/"Rejected") rather than
// hiring-decision ("Hire"/"No Hire"): the AI interview gates who advances, it
// does not make the offer. Exported so the mapping can be asserted directly —
// it is the one place this terminology is defined for both the dashboard and
// the PDF exports.
export const recommendationLabel = (status, score) => {
    if (status === 'Pass')
        return score != null && score >= 85 ? 'Shortlisted — strong' : 'Shortlisted';
    if (status === 'Fail')
        return score != null && score >= 45 ? 'Rejected — borderline' : 'Rejected';
    return 'Pending evaluation';
};
// ── main builder ───────────────────────────────────────────────────────────
export function buildReportViewModel(interview, opts = {}) {
    var _a, _b, _c, _d, _e;
    _uid = 0;
    const iv = interview || {};
    const jd = iv.jd || {};
    const report = iv.final_report || null;
    const hasReport = !!report;
    // ── timeline anchor for video ↔ transcript sync (real timestamps only) ──────
    const startMs = (_a = parseTime(iv.started_at)) !== null && _a !== void 0 ? _a : parseTime(iv.valid_from);
    const endMs = parseTime(iv.ended_at);
    const canSyncVideo = !!iv.recording_url && startMs != null;
    const offsetOf = (ts) => {
        if (!canSyncVideo)
            return null;
        const t = parseTime(ts);
        if (t == null || startMs == null)
            return null;
        return Math.max(0, Math.round((t - startMs) / 1000));
    };
    // ── meta ────────────────────────────────────────────────────────────────
    const userType = opts.userType || {};
    const recDomain = opts.recruiterDomain || '';
    const profileId = userType.staffing_candidate_id || userType.candidate_profile_id;
    const candidateProfileHref = iv.target_service && profileId
        ? `${recDomain}/${userType.staffing_candidate_id ? 'my-' : ''}candidate-profile?id=${profileId}`
        : null;
    const roleHref = iv.target_service
        ? jd.job_reference_id
            ? `${recDomain}/job-post-details?id=${jd.job_reference_id}`
            : null
        : jd.id
            ? `/job-description/${jd.id}`
            : null;
    const stMeta = statusMeta(iv.status);
    const meta = {
        candidateName: iv.interviewee_name || 'Candidate',
        candidateEmail: iv.interviewee_email || '',
        candidatePhone: userType.mobile_number || null,
        candidateProfileHref,
        role: jd.title || iv.title || null,
        roleHref,
        interviewType: iv.interview_type || null,
        interviewLevel: jd.interview_level || iv.interview_level || null,
        // Prefer the actual start time so this matches the "Start Date & Time"
        // shown in the interviews list — `valid_from` is only the scheduled slot,
        // which can differ from when the candidate actually started.
        interviewDate: iv.started_at || iv.valid_from || null,
        scheduledDurationMin: asNum((_b = jd.interview_duration) !== null && _b !== void 0 ? _b : iv.interview_duration),
        actualDurationLabel: startMs != null && endMs != null && endMs > startMs ? fmtElapsed(endMs - startMs) : null,
        status: iv.status || '',
        statusLabel: stMeta.label,
        statusTone: stMeta.tone,
        isComplete: ['pass', 'fail', 'completed'].includes((iv.status || '').toLowerCase()),
        recordingUrl: iv.recording_url || null,
        takenBy: iv.interview_taken_by || null,
    };
    // ── overall score ─────────────────────────────────────────────────────────
    const finalScore = asNum(report === null || report === void 0 ? void 0 : report.final_score);
    const passingScore = asNum(report === null || report === void 0 ? void 0 : report.passing_score);
    const reportStatus = (report === null || report === void 0 ? void 0 : report.status) === 'Pass' || (report === null || report === void 0 ? void 0 : report.status) === 'Fail' ? report.status : null;
    const score = {
        value: finalScore,
        passingScore,
        status: reportStatus,
        recommendationLabel: recommendationLabel(reportStatus, finalScore),
        tone: reportStatus === 'Pass' ? 'success' : reportStatus === 'Fail' ? 'danger' : scoreBand(finalScore).tone,
    };
    // ── criteria (flat evaluation criteria with pass thresholds) ───────────────
    // NOTE: `min_score` in the backend report is the MAX marks for the criterion
    // (weights across all criteria sum to 100) — NOT a pass threshold. So the score
    // is "X out of its max allocation" and there is no honest met/not-met flag.
    const criteria = asArray(report === null || report === void 0 ? void 0 : report.criteria_scores).map((c) => {
        const sc = asNum(c.score);
        const target = asNum(c.min_score);
        const pct = pctOf(sc, target);
        return {
            id: uid('crit'),
            title: c.title || 'Criterion',
            score: sc,
            target,
            pct,
            reason: c.reason || null,
            met: null,
            band: scoreBand(pct),
            isCommunication: /communicat/i.test(c.title || ''),
        };
    });
    // ── evaluated question mapper (shared) ─────────────────────────────────────
    const mapEvalQ = (q) => {
        var _a;
        const sc = asNum(q.score);
        const outOf = asNum((_a = q.out_of) !== null && _a !== void 0 ? _a : q.outOf);
        return {
            id: uid('eq'),
            question: q.question || q.text || q.title || null,
            answer: q.answer || null,
            code: q.code || q.code_snippet || null,
            language: q.language || null,
            reason: q.reason || q.explanation || q.ai_feedback || null,
            score: sc,
            outOf,
            pct: pctOf(sc, outOf),
        };
    };
    // ── objectives (from interview_structure_scores, coding merged server-side) ─
    const structure = asArray(report === null || report === void 0 ? void 0 : report.interview_structure_scores);
    const codingMerged = asArray(iv.interview_structure_with_coding);
    const codingBySubTitle = new Map();
    codingMerged.forEach((o) => asArray(o.sub_objectives).forEach((s) => {
        const key = (s.title || '').trim().toLowerCase();
        if (key && asArray(s.coding_questions).length)
            codingBySubTitle.set(key, asArray(s.coding_questions));
    }));
    const objectives = structure.map((o, i) => {
        const subs = asArray(o.sub_objectives).map((s) => {
            const subScore = asNum(s.score);
            const subCrit = asArray(s.evaluation_criteria).map((c) => {
                const sc = asNum(c.score);
                const target = asNum(c.min_score);
                const pct = pctOf(sc, target);
                return {
                    id: uid('scrit'),
                    title: c.title || 'Criterion',
                    score: sc,
                    target,
                    pct,
                    reason: c.reason || null,
                    met: null,
                    band: scoreBand(pct),
                    isCommunication: /communicat/i.test(c.title || ''),
                };
            });
            const codingQs = (codingBySubTitle.get((s.title || '').trim().toLowerCase()) || []).map(mapEvalQ);
            return {
                id: uid('sub'),
                title: s.title || 'Sub-objective',
                score: subScore,
                weight: asNum(s.weight),
                band: scoreBand(subScore),
                criteria: subCrit,
                questions: asArray(s.evaluated_questions).map(mapEvalQ),
                codingQuestions: codingQs,
            };
        });
        const objScore = asNum(o.score);
        return {
            id: uid('obj'),
            index: i,
            title: o.title || 'Objective',
            score: objScore,
            weight: asNum(o.weight),
            band: scoreBand(objScore),
            subObjectives: subs,
            questionCount: subs.reduce((n, s) => n + s.questions.length + s.codingQuestions.length, 0),
        };
    });
    // ── transcript ─────────────────────────────────────────────────────────────
    const candidateName = meta.candidateName;
    const transcript = asArray(iv.transcript).map((m) => {
        const isInterviewer = m.sender === 'interviewer';
        return {
            id: uid('tr'),
            speaker: isInterviewer ? 'interviewer' : 'candidate',
            speakerName: isInterviewer ? 'Interview Bot' : candidateName,
            text: m.text || '',
            timestamp: m.timestamp || null,
            timeLabel: m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : null,
            videoOffsetSec: offsetOf(m.timestamp),
            type: m.type || null,
            score: asNum(m.score),
            reason: m.reason || null,
            isCoding: !!m.is_coding_question,
            code: m.code_snippet || null,
            proctoringInsight: mapInsight(m.proctoring_insight),
        };
    });
    // ── timeline (derived from real transcript timestamps — no fabricated events) ─
    const tlStart = startMs !== null && startMs !== void 0 ? startMs : parseTime((_c = asArray(iv.transcript)[0]) === null || _c === void 0 ? void 0 : _c.timestamp);
    const timeline = [];
    if (tlStart != null) {
        asArray(iv.transcript).forEach((m) => {
            const t = parseTime(m.timestamp);
            if (t == null)
                return;
            const elapsed = Math.max(0, (t - tlStart) / 1000);
            if (m.type === 'greeting') {
                timeline.push({ id: uid('tl'), label: mmss(elapsed), text: 'Interview started', kind: 'start' });
            }
            else if (m.type === 'question') {
                const txt = (m.text || '').trim();
                timeline.push({
                    id: uid('tl'),
                    label: mmss(elapsed),
                    text: txt.length > 90 ? txt.slice(0, 89) + '…' : txt || 'Question asked',
                    kind: m.is_coding_question ? 'coding' : 'question',
                });
            }
        });
    }
    // ── evidence (primary: evaluated questions inside objectives; carries the
    //    objective/sub-objective mapping. supplemented by scored transcript
    //    answers that aren't already represented). ──────────────────────────────
    const evidence = [];
    objectives.forEach((o) => o.subObjectives.forEach((s) => {
        [...s.questions, ...s.codingQuestions].forEach((q) => {
            var _a;
            const isCoding = !!q.code;
            evidence.push({
                id: uid('ev'),
                question: q.question,
                response: q.answer || q.code,
                code: q.code,
                language: q.language,
                evaluation: q.reason,
                score: q.score,
                outOf: q.outOf,
                pct: q.pct,
                band: scoreBand(q.pct),
                objectiveTitle: o.title,
                subObjectiveTitle: s.title,
                criterionTitle: ((_a = s.criteria[0]) === null || _a === void 0 ? void 0 : _a.title) || null,
                timestamp: null,
                videoOffsetSec: null,
                isCoding,
                source: isCoding ? 'coding' : 'objective',
            });
        });
    }));
    // Supplement with scored candidate answers from the transcript (real per-answer
    // scores) — only when objective-level evidence is thin, so we never duplicate.
    if (evidence.length === 0) {
        transcript.forEach((t, i) => {
            var _a;
            if (t.speaker === 'candidate' && (t.score != null || t.reason)) {
                const q = ((_a = transcript[i - 1]) === null || _a === void 0 ? void 0 : _a.speaker) === 'interviewer' ? transcript[i - 1] : null;
                evidence.push({
                    id: uid('ev'),
                    question: (q === null || q === void 0 ? void 0 : q.text) || null,
                    response: t.text,
                    code: t.code,
                    language: null,
                    evaluation: t.reason,
                    score: t.score,
                    outOf: t.score != null ? 100 : null,
                    pct: t.score,
                    band: scoreBand(t.score),
                    objectiveTitle: null,
                    subObjectiveTitle: null,
                    criterionTitle: null,
                    timestamp: t.timestamp,
                    videoOffsetSec: t.videoOffsetSec,
                    isCoding: t.isCoding,
                    source: 'transcript',
                });
            }
        });
    }
    // ── coding items (dedicated coding tab) ────────────────────────────────────
    const coding = [];
    asArray(iv.coding_que_and_ans_data).forEach((q) => {
        var _a;
        const sc = asNum(q.score);
        const outOf = asNum((_a = q.out_of) !== null && _a !== void 0 ? _a : q.outOf);
        coding.push({
            id: uid('code'),
            title: q.title || q.sub_objective_title || null,
            question: q.question || q.objective_text || q.description || null,
            code: q.code || q.code_snippet || q.answer || null,
            language: q.language || null,
            evaluation: q.reason || q.explanation || null,
            score: sc,
            outOf,
            pct: pctOf(sc, outOf),
            band: scoreBand(pctOf(sc, outOf)),
            syntaxValid: typeof q.syntax_valid === 'boolean' ? q.syntax_valid : null,
            testCasesPassed: asNum(q.test_cases_passed),
            totalTestCases: asNum(q.total_test_cases),
            aiFeedback: q.ai_feedback || null,
            objectiveTitle: q.objective_title || null,
            subObjectiveTitle: q.sub_objective_title || null,
        });
    });
    // Fallback: coding entries embedded in the transcript.
    if (coding.length === 0) {
        transcript.forEach((t) => {
            if (t.isCoding && t.code) {
                coding.push({
                    id: uid('code'),
                    title: null,
                    question: null,
                    code: t.code,
                    language: null,
                    evaluation: t.reason,
                    score: t.score,
                    outOf: t.score != null ? 100 : null,
                    pct: t.score,
                    band: scoreBand(t.score),
                    syntaxValid: null,
                    testCasesPassed: null,
                    totalTestCases: null,
                    aiFeedback: null,
                    objectiveTitle: null,
                    subObjectiveTitle: null,
                });
            }
        });
    }
    // ── behaviour (soft-skill criteria surfaced honestly from real criteria) ────
    const behaviourRe = /communicat|behav|attitude|confiden|clarity|articul|interpersonal|collaborat/i;
    const behaviour = criteria.filter((c) => behaviourRe.test(c.title));
    // ── proctoring (structured cards from real counts) ──────────────────────────
    const cds = iv.cheating_detector_summary || (report === null || report === void 0 ? void 0 : report.cheating_detector_summary) || {};
    const proctorDefs = [
        { key: 'multiple_faces', label: 'Multiple Faces', field: 'multiple_faces_count' },
        { key: 'left_frame', label: 'Left Frame', field: 'disappear_from_cam_count' },
        { key: 'tab_switch', label: 'Tab Switches', field: 'total_tab_switch_count' },
        { key: 'camera_stopped', label: 'Camera Stops', field: 'camera_stopped_count' },
        { key: 'eye_movement', label: 'Eye Movements', field: 'eye_movement_count' },
        { key: 'head_movement', label: 'Head Movements', field: 'head_movement_count' },
    ];
    const proctorCards = proctorDefs
        .filter((d) => cds[d.field] !== undefined && cds[d.field] !== null)
        .map((d) => ({ key: d.key, label: d.label, value: Number(cds[d.field]) || 0 }));
    const summaryText = ((_d = report === null || report === void 0 ? void 0 : report.evaluation_of_cheating_detection) === null || _d === void 0 ? void 0 : _d.summary) || '';
    const summarySentences = summaryText
        ? summaryText.split(/(?<=[.!?])\s+/).filter((s) => s.trim())
        : [];
    // ── holistic engine outputs (post-interview batch) ──────────────────────────
    const overall = (report === null || report === void 0 ? void 0 : report.proctoring_overall) || {};
    const integrity = overall.integrity || {};
    const overallBody = overall.body_language || (report === null || report === void 0 ? void 0 : report.body_language_summary) || {};
    const overallConf = overall.confidence || (report === null || report === void 0 ? void 0 : report.confidence_summary) || {};
    const verdict = integrity.verdict || null;
    const VERDICT_META = {
        no_concern: { label: 'No integrity concerns', tone: 'success' },
        review: { label: 'Review recommended', tone: 'warning' },
        concern: { label: 'Integrity concern', tone: 'danger' },
    };
    const vMeta = (verdict && VERDICT_META[verdict]) || { label: '', tone: 'neutral' };
    const events = asArray(report === null || report === void 0 ? void 0 : report.proctoring_events).map((e) => ({
        type: e.type || 'event',
        label: e.label || e.type || 'Event',
        start: Number(e.start) || 0,
        end: Number(e.end) || 0,
        durationSec: Number(e.duration_sec) || 0,
        peakMagnitude: Number(e.peak_magnitude) || 0,
        unit: e.unit || '',
        severity: (['low', 'medium', 'high'].includes(e.severity) ? e.severity : 'low'),
        integrity: !!e.integrity,
    }));
    const bodyLanguage = overallBody && Object.keys(overallBody).length
        ? {
            available: !!overallBody.available,
            posture: overallBody.posture || 'unknown',
            openness: overallBody.openness || 'unknown',
            fidget: overallBody.fidget_level || 'unknown',
        }
        : null;
    const confAttrs = (overallConf.attributes || {});
    const confidence = overallConf && (overallConf.overall_score != null || overallConf.band)
        ? {
            score: asNum(overallConf.overall_score),
            band: overallConf.band || null,
            trend: overallConf.trend || null,
            drivers: asArray(overallConf.drivers),
            attributes: Object.keys(confAttrs).map((k) => ({ key: k, value: Number(confAttrs[k]) || 0 })),
            disclaimer: overall.disclaimer ||
                'Confidence and body-language values are automated estimates for reviewer context only — not competency scores.',
        }
        : null;
    const capture = asArray(report === null || report === void 0 ? void 0 : report.proctoring_capture);
    // OV-1: sort by timestamp so findLayoutAt's binary search is correct and a
    // later transition (e.g. the code-editor PiP) can never be back-applied to the
    // interview's opening seconds. Rows should arrive monotonically, but a
    // reconnect/replay or out-of-order delivery could break the ordering the
    // binary search relies on.
    const layout = asArray(report === null || report === void 0 ? void 0 : report.proctoring_layout)
        .slice()
        .sort((a, b) => (Number(a === null || a === void 0 ? void 0 : a.t) || 0) - (Number(b === null || b === void 0 ? void 0 : b.t) || 0));
    const baseline = (report === null || report === void 0 ? void 0 : report.proctoring_baseline) || null;
    const series = asArray(report === null || report === void 0 ? void 0 : report.proctoring_series);
    // event-type → human label (shared by charts)
    const EVENT_LABELS = {
        looking_down: 'Looking Down',
        looking_away: 'Looking Away',
        eye_movement: 'Gaze Off-screen',
        multiple_faces: 'Multiple Faces',
        disappear_from_cam: 'Camera Missing',
        eyes_closed: 'Eyes Closed',
        talking: 'Mouth Movement',
        hand_near_face: 'Hand Near Face',
        slouching: 'Slouching',
    };
    const labelOf = (t) => EVENT_LABELS[t] || t.replace(/_/g, ' ');
    const GAZE_LABELS = {
        center: 'Center',
        left: 'Left',
        right: 'Right',
        up: 'Up',
        down: 'Down',
    };
    const rawCharts = overall.charts || {};
    const charts = {
        eventsByType: asArray(rawCharts.events_by_type).map((e) => ({
            type: e.type,
            label: labelOf(e.type),
            count: Number(e.count) || 0,
        })),
        severityDistribution: asArray(rawCharts.severity_distribution).map((s) => ({
            severity: s.severity,
            count: Number(s.count) || 0,
        })),
        riskBreakdown: asArray(rawCharts.risk_breakdown).map((r) => ({
            type: r.type,
            label: labelOf(r.type),
            severity: r.severity,
            pct: Number(r.pct) || 0,
        })),
        gazeDistribution: asArray(rawCharts.gaze_distribution).map((g) => ({
            dir: g.dir,
            label: GAZE_LABELS[g.dir] || g.dir,
            pct: Number(g.pct) || 0,
        })),
        confidenceTrend: asArray(rawCharts.confidence_trend).map((c) => ({
            t: Number(c.t) || 0,
            score: Number(c.score) || 0,
        })),
        facePresencePct: asNum(rawCharts.face_presence_pct),
        singlePersonPct: asNum(rawCharts.single_person_pct),
        totalEvents: Number(rawCharts.total_events) || events.length,
        eventCategories: Number(rawCharts.event_categories) || 0,
    };
    const integrityScore = asNum(integrity.score);
    const proctoring = {
        available: proctorCards.length > 0 ||
            summarySentences.length > 0 ||
            events.length > 0 ||
            !!verdict ||
            !!confidence,
        cards: proctorCards,
        summarySentences,
        totalFrames: (_e = asNum(cds.total_frames)) !== null && _e !== void 0 ? _e : asNum(overall.total_frames),
        verdict,
        verdictLabel: vMeta.label || null,
        verdictTone: vMeta.tone,
        verdictReasons: asArray(integrity.reasons),
        totalFlaggedSec: asNum(integrity.total_flagged_sec),
        reviewRecommended: !!integrity.review_recommended,
        integrityScore,
        integrityBand: integrityScore == null ? null : integrityScore >= 80 ? 'Good' : integrityScore >= 55 ? 'Fair' : 'Poor',
        tabSwitches: asNum(cds.total_tab_switch_count),
        events,
        bodyLanguage,
        confidence,
        capture,
        layout,
        baseline,
        series,
        charts,
    };
    // ── counts (only populate what is genuinely derivable) ──────────────────────
    const questionsAsked = transcript.filter((t) => t.type === 'question').length;
    const subObjCount = objectives.reduce((n, o) => n + o.subObjectives.length, 0);
    const counts = {
        questionsAsked: questionsAsked || null,
        objectives: objectives.length || null,
        subObjectives: subObjCount || null,
        criteria: criteria.length || null,
        criteriaMet: null,
        evidence: evidence.length || null,
        codingChallenges: coding.length || null,
    };
    return {
        hasReport,
        meta,
        score,
        summary: {
            reason: (report === null || report === void 0 ? void 0 : report.reason) || null,
            strengths: asArray(report === null || report === void 0 ? void 0 : report.key_strengths).filter(Boolean),
            improvements: asArray(report === null || report === void 0 ? void 0 : report.areas_to_improve).filter(Boolean),
        },
        counts,
        criteria,
        objectives,
        evidence,
        transcript,
        timeline,
        coding,
        behaviour,
        proctoring,
    };
}
